import { rotatePoint } from "./layerRotation.js";

export const ALIGNMENT_SNAP_ENGAGE_PX = 6;
export const ALIGNMENT_SNAP_RELEASE_PX = 14;

export const isAlignmentSnapHeldOff = (event) => Boolean(event?.altKey);

const toNumber = (value) => Number(value) || 0;

const MIN_BOX_PX = 0.5;

export const boxesOutsideDraggedBranch = ({ boxes = [], excludeId }) => {
  if (!excludeId) return boxes;
  const byId = new Map(boxes.map((box) => [box.id, box]));
  const isDraggedBranch = (box) => {
    let current = box;
    const seen = new Set();
    while (current?.id && !seen.has(current.id)) {
      if (current.id === excludeId) return true;
      seen.add(current.id);
      current = current.parentID ? byId.get(current.parentID) : null;
    }
    return false;
  };
  return boxes.filter((box) => !isDraggedBranch(box));
};

export const axisAlignedBounds = (box) => {
  const x = toNumber(box?.x);
  const y = toNumber(box?.y);
  const width = toNumber(box?.width);
  const height = toNumber(box?.height);
  const rotation = toNumber(box?.rotation);
  if (!rotation) {
    return { x, y, width, height };
  }
  const center = { x: x + width / 2, y: y + height / 2 };
  const corners = [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ].map((point) => rotatePoint({ point, center, degrees: rotation }));
  const xs = corners.map((corner) => corner.x);
  const ys = corners.map((corner) => corner.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

const xEdges = (bounds) => ({
  left: bounds.x,
  centerX: bounds.x + bounds.width / 2,
  right: bounds.x + bounds.width,
});

const yEdges = (bounds) => ({
  top: bounds.y,
  centerY: bounds.y + bounds.height / 2,
  bottom: bounds.y + bounds.height,
});

const collectAxisTargets = ({
  otherBoxes,
  artboardSize,
  artboardEdges,
  boxEdges,
}) => {
  const targets = [];
  otherBoxes.forEach((box) => {
    const bounds = axisAlignedBounds(box);
    if (bounds.width < MIN_BOX_PX || bounds.height < MIN_BOX_PX) return;
    const edges = boxEdges(bounds);
    Object.entries(edges).forEach(([edge, value]) => {
      targets.push({
        value,
        kind: "layer",
        edge,
        sourceBox: bounds,
      });
    });
  });
  Object.entries(artboardEdges(artboardSize)).forEach(([edge, value]) => {
    targets.push({
      value,
      kind: "artboard",
      edge,
      sourceBox: null,
    });
  });
  return targets;
};

const collectTargets = ({
  otherBoxes,
  artboardWidth,
  artboardHeight,
  seamOffsets = [],
}) => ({
  vertical: [
    ...collectAxisTargets({
      otherBoxes,
      artboardSize: artboardWidth,
      artboardEdges: (width) => ({
        left: 0,
        centerX: width / 2,
        right: width,
      }),
      boxEdges: xEdges,
    }),
    ...seamOffsets.map((value) => ({
      value,
      kind: "seam",
      edge: "seam",
      sourceBox: null,
    })),
  ],
  horizontal: collectAxisTargets({
    otherBoxes,
    artboardSize: artboardHeight,
    artboardEdges: (height) => ({
      top: 0,
      centerY: height / 2,
      bottom: height,
    }),
    boxEdges: yEdges,
  }),
});

const pickAxisSnap = ({ edges, targets, prevSnap, engagePx, releasePx }) => {
  if (prevSnap?.movingEdge && prevSnap.movingEdge in edges) {
    const currentValue = edges[prevSnap.movingEdge];
    if (Math.abs(prevSnap.targetValue - currentValue) <= releasePx) {
      return {
        movingEdge: prevSnap.movingEdge,
        targetValue: prevSnap.targetValue,
        kind: prevSnap.kind,
        delta: prevSnap.targetValue - currentValue,
      };
    }
  }

  let best = null;
  Object.entries(edges).forEach(([movingEdge, value]) => {
    targets.forEach((target) => {
      const dist = Math.abs(target.value - value);
      if (dist > engagePx) return;
      if (best && dist >= best.dist) return;
      best = {
        movingEdge,
        targetValue: target.value,
        kind: target.kind,
        delta: target.value - value,
        dist,
      };
    });
  });
  if (!best) return null;
  return {
    movingEdge: best.movingEdge,
    targetValue: best.targetValue,
    kind: best.kind,
    delta: best.delta,
  };
};

const spanForSnap = ({
  axis,
  targetValue,
  movingBounds,
  targets,
  artboardWidth,
  artboardHeight,
}) => {
  let start = axis === "x" ? movingBounds.y : movingBounds.x;
  let end =
    axis === "x"
      ? movingBounds.y + movingBounds.height
      : movingBounds.x + movingBounds.width;
  let hitArtboard = false;
  targets.forEach((target) => {
    if (Math.abs(target.value - targetValue) > 0.51) return;
    if (target.kind === "artboard" || target.kind === "seam") {
      hitArtboard = true;
      return;
    }
    const source = target.sourceBox;
    if (!source) return;
    if (axis === "x") {
      start = Math.min(start, source.y);
      end = Math.max(end, source.y + source.height);
    } else {
      start = Math.min(start, source.x);
      end = Math.max(end, source.x + source.width);
    }
  });
  if (hitArtboard) {
    return axis === "x"
      ? { start: 0, end: artboardHeight }
      : { start: 0, end: artboardWidth };
  }
  return { start, end };
};

const guidesFromSnaps = ({
  xSnap,
  ySnap,
  movingBounds,
  targets,
  artboardWidth,
  artboardHeight,
}) => {
  const guides = [];
  if (xSnap) {
    const span = spanForSnap({
      axis: "x",
      targetValue: xSnap.targetValue,
      movingBounds,
      targets: targets.vertical,
      artboardWidth,
      artboardHeight,
    });
    guides.push({
      axis: "x",
      position: xSnap.targetValue,
      start: span.start,
      end: span.end,
    });
  }
  if (ySnap) {
    const span = spanForSnap({
      axis: "y",
      targetValue: ySnap.targetValue,
      movingBounds,
      targets: targets.horizontal,
      artboardWidth,
      artboardHeight,
    });
    guides.push({
      axis: "y",
      position: ySnap.targetValue,
      start: span.start,
      end: span.end,
    });
  }
  return guides;
};

const emptyResult = (box) => ({ box, snap: null, guides: [] });

const resizeEdgesForCorner = (corner) => {
  const handle = String(corner || "");
  return {
    moveLeft: handle.includes("w"),
    moveRight: handle.includes("e"),
    moveTop: handle.includes("n"),
    moveBottom: handle.includes("s"),
  };
};

const applyResizeSnap = ({ box, snap, minSize, axis }) => {
  if (!snap) return { box, applied: false };
  if (axis === "x") {
    if (snap.movingEdge === "left") {
      const right = box.x + box.width;
      const nextX = snap.targetValue;
      const nextWidth = right - nextX;
      if (nextWidth < minSize) return { box, applied: false };
      return { box: { ...box, x: nextX, width: nextWidth }, applied: true };
    }
    if (snap.movingEdge === "right") {
      const nextWidth = snap.targetValue - box.x;
      if (nextWidth < minSize) return { box, applied: false };
      return { box: { ...box, width: nextWidth }, applied: true };
    }
    return { box, applied: false };
  }
  if (snap.movingEdge === "top") {
    const bottom = box.y + box.height;
    const nextY = snap.targetValue;
    const nextHeight = bottom - nextY;
    if (nextHeight < minSize) return { box, applied: false };
    return { box: { ...box, y: nextY, height: nextHeight }, applied: true };
  }
  if (snap.movingEdge === "bottom") {
    const nextHeight = snap.targetValue - box.y;
    if (nextHeight < minSize) return { box, applied: false };
    return { box: { ...box, height: nextHeight }, applied: true };
  }
  return { box, applied: false };
};

const isAspectLockCorner = (corner) => {
  const handle = String(corner || "");
  return (
    (handle.includes("w") || handle.includes("e")) &&
    (handle.includes("n") || handle.includes("s"))
  );
};

const scaleForEdgeSnap = ({ box, snap, axis }) => {
  if (!snap || !box?.width || !box?.height) return null;
  let scale = null;
  if (axis === "x") {
    if (snap.movingEdge === "right") {
      scale = (snap.targetValue - box.x) / box.width;
    } else if (snap.movingEdge === "left") {
      scale = (box.x + box.width - snap.targetValue) / box.width;
    }
  } else if (snap.movingEdge === "bottom") {
    scale = (snap.targetValue - box.y) / box.height;
  } else if (snap.movingEdge === "top") {
    scale = (box.y + box.height - snap.targetValue) / box.height;
  }
  if (!Number.isFinite(scale) || scale <= 0) return null;
  return scale;
};

const scaleBoxFromAnchor = ({ box, scale, corner, minSize }) => {
  const handle = String(corner || "");
  const fromWest = handle.includes("w");
  const fromNorth = handle.includes("n");
  const originRight = box.x + box.width;
  const originBottom = box.y + box.height;
  const nextWidth = box.width * scale;
  const nextHeight = box.height * scale;
  if (nextWidth < minSize || nextHeight < minSize) return null;
  return {
    ...box,
    x: fromWest ? originRight - nextWidth : box.x,
    y: fromNorth ? originBottom - nextHeight : box.y,
    width: nextWidth,
    height: nextHeight,
  };
};

const snapIfEdgeAligned = ({ edges, targets }) => {
  let best = null;
  Object.entries(edges).forEach(([movingEdge, value]) => {
    targets.forEach((target) => {
      const dist = Math.abs(target.value - value);
      if (dist > 0.51) return;
      if (best && dist >= best.dist) return;
      best = {
        movingEdge,
        targetValue: target.value,
        kind: target.kind,
        delta: target.value - value,
        dist,
      };
    });
  });
  if (!best) return null;
  return {
    movingEdge: best.movingEdge,
    targetValue: best.targetValue,
    kind: best.kind,
    delta: best.delta,
  };
};

const applyAspectLockedResizeSnap = ({
  box,
  corner,
  xSnap,
  ySnap,
  prevLockAxis,
  minSize,
  targets,
}) => {
  const candidates = [];
  if (xSnap) {
    const scale = scaleForEdgeSnap({ box, snap: xSnap, axis: "x" });
    const nextBox =
      scale == null
        ? null
        : scaleBoxFromAnchor({ box, scale, corner, minSize });
    if (nextBox) {
      candidates.push({
        box: nextBox,
        lockAxis: "x",
        dist: Math.abs(xSnap.delta),
        xSnap,
        ySnap: null,
      });
    }
  }
  if (ySnap) {
    const scale = scaleForEdgeSnap({ box, snap: ySnap, axis: "y" });
    const nextBox =
      scale == null
        ? null
        : scaleBoxFromAnchor({ box, scale, corner, minSize });
    if (nextBox) {
      candidates.push({
        box: nextBox,
        lockAxis: "y",
        dist: Math.abs(ySnap.delta),
        xSnap: null,
        ySnap,
      });
    }
  }
  if (!candidates.length) {
    return { box, xSnap: null, ySnap: null, lockAxis: null };
  }

  const held = candidates.find(
    (candidate) => candidate.lockAxis === prevLockAxis
  );
  const winner =
    held ||
    candidates.reduce((best, candidate) =>
      candidate.dist < best.dist ? candidate : best
    );

  const snappedBounds = axisAlignedBounds(winner.box);
  const { moveLeft, moveRight, moveTop, moveBottom } =
    resizeEdgesForCorner(corner);
  const xEdgesToCheck = {};
  const yEdgesToCheck = {};
  if (moveLeft) xEdgesToCheck.left = snappedBounds.x;
  if (moveRight) xEdgesToCheck.right = snappedBounds.x + snappedBounds.width;
  if (moveTop) yEdgesToCheck.top = snappedBounds.y;
  if (moveBottom) yEdgesToCheck.bottom = snappedBounds.y + snappedBounds.height;
  const appliedXSnap =
    winner.xSnap ||
    snapIfEdgeAligned({
      edges: xEdgesToCheck,
      targets: targets.vertical,
    });
  const appliedYSnap =
    winner.ySnap ||
    snapIfEdgeAligned({
      edges: yEdgesToCheck,
      targets: targets.horizontal,
    });

  return {
    box: winner.box,
    xSnap: appliedXSnap,
    ySnap: appliedYSnap,
    lockAxis: winner.lockAxis,
  };
};

export const layoutGuidesToVisual = ({ guides = [], zoom = 1 }) => {
  if (!guides.length || Math.abs(Number(zoom) - 1) < 0.001) return guides;
  return guides.map((guide) => ({
    ...guide,
    position: guide.position * zoom,
    start: guide.start * zoom,
    end: guide.end * zoom,
  }));
};

export const snapBoxToGuides = ({
  box,
  mode = "move",
  corner = "",
  otherBoxes = [],
  artboardWidth,
  artboardHeight,
  seamOffsets = [],
  prevSnap = null,
  minSize = 8,
  engagePx = ALIGNMENT_SNAP_ENGAGE_PX,
  releasePx = ALIGNMENT_SNAP_RELEASE_PX,
  disabled = false,
}) => {
  if (
    disabled ||
    !box ||
    !artboardWidth ||
    !artboardHeight ||
    mode === "rotate"
  ) {
    return emptyResult(box);
  }

  switch (mode) {
    case "move":
    case "resize":
      break;
    default: {
      const exhaustive = mode;
      throw new Error(`Unhandled alignment snap mode: ${exhaustive}`);
    }
  }

  if (mode === "resize" && toNumber(box.rotation)) {
    return emptyResult(box);
  }

  const targets = collectTargets({
    otherBoxes,
    artboardWidth,
    artboardHeight,
    seamOffsets,
  });
  const movingBounds = axisAlignedBounds(box);

  if (mode === "move") {
    const xSnap = pickAxisSnap({
      edges: xEdges(movingBounds),
      targets: targets.vertical,
      prevSnap: prevSnap?.x,
      engagePx,
      releasePx,
    });
    const ySnap = pickAxisSnap({
      edges: yEdges(movingBounds),
      targets: targets.horizontal,
      prevSnap: prevSnap?.y,
      engagePx,
      releasePx,
    });
    const nextBox = {
      ...box,
      x: box.x + (xSnap?.delta || 0),
      y: box.y + (ySnap?.delta || 0),
    };
    const snappedBounds = axisAlignedBounds(nextBox);
    return {
      box: nextBox,
      snap: { x: xSnap, y: ySnap },
      guides: guidesFromSnaps({
        xSnap,
        ySnap,
        movingBounds: snappedBounds,
        targets,
        artboardWidth,
        artboardHeight,
      }),
    };
  }

  const { moveLeft, moveRight, moveTop, moveBottom } =
    resizeEdgesForCorner(corner);
  const xEdgesToSnap = {};
  const yEdgesToSnap = {};
  if (moveLeft) xEdgesToSnap.left = movingBounds.x;
  if (moveRight) xEdgesToSnap.right = movingBounds.x + movingBounds.width;
  if (moveTop) yEdgesToSnap.top = movingBounds.y;
  if (moveBottom) yEdgesToSnap.bottom = movingBounds.y + movingBounds.height;

  const xSnap = Object.keys(xEdgesToSnap).length
    ? pickAxisSnap({
        edges: xEdgesToSnap,
        targets: targets.vertical,
        prevSnap: prevSnap?.x,
        engagePx,
        releasePx,
      })
    : null;
  const ySnap = Object.keys(yEdgesToSnap).length
    ? pickAxisSnap({
        edges: yEdgesToSnap,
        targets: targets.horizontal,
        prevSnap: prevSnap?.y,
        engagePx,
        releasePx,
      })
    : null;

  if (isAspectLockCorner(corner)) {
    const locked = applyAspectLockedResizeSnap({
      box,
      corner,
      xSnap,
      ySnap,
      prevLockAxis: prevSnap?.lockAxis,
      minSize,
      targets,
    });
    const snappedBounds = axisAlignedBounds(locked.box);
    return {
      box: locked.box,
      snap: { x: locked.xSnap, y: locked.ySnap, lockAxis: locked.lockAxis },
      guides: guidesFromSnaps({
        xSnap: locked.xSnap,
        ySnap: locked.ySnap,
        movingBounds: snappedBounds,
        targets,
        artboardWidth,
        artboardHeight,
      }),
    };
  }

  const xResize = applyResizeSnap({
    box,
    snap: xSnap,
    minSize,
    axis: "x",
  });
  const yResize = applyResizeSnap({
    box: xResize.box,
    snap: ySnap,
    minSize,
    axis: "y",
  });
  const nextBox = yResize.box;
  const appliedXSnap = xResize.applied ? xSnap : null;
  const appliedYSnap = yResize.applied ? ySnap : null;
  const snappedBounds = axisAlignedBounds(nextBox);
  return {
    box: nextBox,
    snap: { x: appliedXSnap, y: appliedYSnap },
    guides: guidesFromSnaps({
      xSnap: appliedXSnap,
      ySnap: appliedYSnap,
      movingBounds: snappedBounds,
      targets,
      artboardWidth,
      artboardHeight,
    }),
  };
};

export const applyDragAlignmentSnap = ({
  box,
  mode,
  corner,
  event,
  excludeId,
  prevSnap,
  snapContext,
  guidesRef,
  enabled = true,
}) => {
  const guideScale = Number(snapContext?.guideScale) || 1;
  const toLayout = guideScale > 0.001 ? 1 / guideScale : 1;
  const result = snapBoxToGuides({
    box,
    mode,
    corner,
    otherBoxes: snapContext?.getOtherBoxes?.(excludeId) || [],
    artboardWidth: snapContext?.artboardWidth,
    artboardHeight: snapContext?.artboardHeight,
    seamOffsets: snapContext?.seamOffsets,
    prevSnap,
    minSize: (snapContext?.minSize ?? 8) * toLayout,
    engagePx: ALIGNMENT_SNAP_ENGAGE_PX * toLayout,
    releasePx: ALIGNMENT_SNAP_RELEASE_PX * toLayout,
    disabled: !enabled || isAlignmentSnapHeldOff(event),
  });
  guidesRef?.current?.setGuides(
    layoutGuidesToVisual({ guides: result.guides, zoom: guideScale })
  );
  return result;
};
